import * as React from "react";
import {
  Column,
  useTable,
  HeaderGroup,
  TableState,
  usePagination,
} from "react-table";
import { LeftOutlined } from "@ant-design/icons";
import { RightOutlined } from "@ant-design/icons";
import classNames from "classnames";
import classes from "./Table.module.scss";
import NoDataComponent from "../NoDataComponent";

// export type EnhancedColumn<T extends object> = Column<T> & {
//   headerProps?: React.HTMLAttributes<HTMLElement>;
// };

export type Props<T extends object> = {
  data: T[];
  columns: any;
  pageSize?: number;
  color?: {
    header?: string;
    headerText?: string;
  };
  grayPagination?: boolean;
  showPagination?: boolean;
  customRowStyle?: object;
  customCellStyle?: object;
};

export default function Table<T extends object>({
  columns,
  data,
  color,
  pageSize = 10,
  grayPagination = false,
  showPagination = true,
  customRowStyle = {},
  customCellStyle = {},
}: Props<T>) {
  const tableInstance = useTable<T>(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize } as Partial<TableState<T>>,
    },
    usePagination
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    canPreviousPage: canViewPreviousPage,
    canNextPage: canViewNextPage,
    previousPage,
    nextPage,
    gotoPage,
    state,
    pageOptions,
  }: any = tableInstance;

  const currentPage = state.pageIndex + 1;
  const rowStart = (currentPage - 1) * pageSize + 1;
  const rowEnd =
    currentPage * pageSize > data.length ? rows.length : currentPage * pageSize;

  const pageNumbers = pageOptions.map((num: number) => num + 1);
  const pageRange = getPageRange(pageNumbers, currentPage);

  if (!data || !data.length)
    return (
      <div className="h-[306px]">
        <NoDataComponent />
      </div>
    );

  return (
    <div className={classes.tableContainer}>
      <table {...getTableProps()} className={classes.table}>
        <thead>
          {headerGroups.map((headerGroup: HeaderGroup<T>, index: number) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              key={index}
              className={classes.tableHeader}
              style={{
                backgroundColor: color?.header ?? "#F8F9FA",
                color: color?.headerText ?? "#1C1F22",
              }}
            >
              {headerGroup.headers.map((column: any) => (
                <th
                  className={classes.headerCell}
                  {...column.getHeaderProps(column.headerProps)}
                  key={column.id}
                >
                  {column.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()} className={classes.tableBody}>
          {page.map((row: any) => {
            prepareRow(row);
            return (
              <tr
                className={classes.row}
                {...row.getRowProps()}
                style={customRowStyle}
                key={row.id}
              >
                {row.cells.map((cell: any, index: number) => {
                  return (
                    <td
                      className={classes.rowCell}
                      {...cell.getCellProps()}
                      key={index}
                      style={customCellStyle}
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {showPagination && (
        <div
          className={classNames(classes.paginationContainer, {
            [classes.grayPagination]: grayPagination,
          })}
        >
          <div className={classes.pageInfo}>
            <span>Showing</span>
            <span>
              {rowStart}
              &ndash;
              {rowEnd}
            </span>
            <span> of</span>
            <span>{rows.length}</span>
          </div>
          <div className={classes.controls}>
            <button
              className={classes.handle}
              onClick={() => previousPage()}
              disabled={!canViewPreviousPage}
            >
              <LeftOutlined />
            </button>

            {pageRange.map((page) => (
              <button
                key={page}
                className={classes.page}
                onClick={() => gotoPage(page - 1)}
                style={{
                  borderInline: page === currentPage ? "1px solid #eaeaea" : "",
                  color:
                    page === currentPage
                      ? color?.header ?? "#2F69CC"
                      : "#1c1f22",
                }}
              >
                {page}
              </button>
            ))}

            <button
              className={classes.handle}
              onClick={() => nextPage()}
              disabled={!canViewNextPage}
            >
              <RightOutlined />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getPageRange(pages: number[], currentPage: number) {
  const RANGE = 3;
  const pageCount = pages.length;
  let displayCount = 0;
  const pageIndex = pages.findIndex((page) => page === currentPage);

  let start;
  let end;

  const isFirstPage = pageIndex === 0;
  const isLastPage = pageIndex + 1 === pageCount;

  while (displayCount < RANGE) {
    if (isFirstPage) {
      start = pageIndex;

      if (pageCount < RANGE) {
        end = pageCount;
      } else {
        end = pageIndex + RANGE;
      }
    } else if (isLastPage) {
      end = pageIndex + 1;

      if (pageCount < RANGE) {
        start = 0;
      } else {
        start = pageIndex - RANGE + 1;
      }
    } else {
      start = pageIndex - 1;
      end = pageIndex + 2;
    }

    displayCount++;
  }

  return pages.slice(start, end);
}
